/**
 * Email Creation Wizard Component
 * Two-page wizard with full width layout and modal for dynamic fields
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { canUseStorage } from '../../hooks/useCookieConsent';
import RichTextEditor from '../ui/RichTextEditor';
import { useUserPermissions } from '../../hooks/useAuth';
import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { EmailPreviewModal } from './EmailPreviewModal';
import { Alert, AlertDescription } from '../ui/alert';
import DynamicFieldsSidebar from './DynamicFieldsSidebar';
import RecipientSelector from './RecipientSelector';
import { AttachmentUploader } from './AttachmentUploader';
import {
  Mail,
  Send,
  Eye,
  Save,
  ArrowLeft,
  Users,
  Type,
  X,
  AlertCircle,
  Search,
  Loader2,
  Bus,
  Award,
  Home,
  UserCheck,
} from 'lucide-react';

export function EmailCreationWizard() {
  useUserPermissions();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { draftUuid } = useParams();
  const [searchParams] = useSearchParams();
  const editorRef = useRef(null);
  const { toast } = useToast();

  // Email state
  const [email, setEmail] = useState({
    email_type: '',
    subject: '',
    content: '',
    recipients: [],
    template_used: '',
    draft_uuid: null // For copying attachments when resending
  });

  // UI state - start on page from URL param (default 1)
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const [wizardPage, setWizardPage] = useState(initialPage); // 1 = Content, 2 = Recipients
  const [showDynamicFieldsModal, setShowDynamicFieldsModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Test email student selector state
  const [recipientFilters, setRecipientFilters] = useState({ status: 'ACTIVE' });
  const [testStudentUuid, setTestStudentUuid] = useState(null);
  const [testStudentSearch, setTestStudentSearch] = useState('');
  const [testStudentList, setTestStudentList] = useState([]);
  const [testStudentLoading, setTestStudentLoading] = useState(false);

  // Draft state
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);

  // Stable callback so RecipientSelector's useEffect doesn't re-fire endlessly
  const handleFiltersChange = useCallback((filters) => {
    setRecipientFilters(filters);
  }, []);

  // Attachment state
  const [attachments, setAttachments] = useState([]);

  // Load draft if draftUuid is provided in URL
  useEffect(() => {
    const loadDraft = async () => {
      if (draftUuid) {
        try {
          setLoading(true);
          const draft = await api.emails.getById(draftUuid);

          // Populate form with draft data
          setEmail({
            email_type: draft.email_type || '',
            subject: draft.subject || '',
            content: draft.body_preview || '', // Assuming body_preview contains the content
            recipients: [], // Recipients will be handled separately if needed
            template_used: draft.template_used || ''
          });

          setCurrentDraftId(draftUuid);

          // Load attachments if draft has any
          if (draft.attachments && draft.attachments.length > 0) {
            setAttachments(draft.attachments);
          }

          toast({
            title: t('email.draftLoaded'),
            description: t('email.draftLoadedDescription'),
            variant: "info",
            duration: 3000,
          });

        } catch (error) {
          // Error loading draft - handled by toast notification
          toast({
            title: t('email.draftLoadFailed'),
            description: t('email.draftLoadFailedDescription'),
            variant: "destructive",
            duration: 5000,
          });
          // Redirect back to communications if draft can't be loaded
          navigate('/communications');
        } finally {
          setLoading(false);
        }
      }
    };

    loadDraft();
  }, [draftUuid, t, toast, navigate]);

  // Fetch students for test email picker when dialog opens or search changes
  useEffect(() => {
    if (!showTestDialog) return;

    let cancelled = false;
    const fetchStudents = async () => {
      setTestStudentLoading(true);
      try {
        const response = await api.students.getAll({
          ...recipientFilters,
          search: testStudentSearch || undefined,
          page_size: 30,
          page: 1,
        });
        if (!cancelled) {
          setTestStudentList(response.results || []);
        }
      } catch {
        if (!cancelled) setTestStudentList([]);
      } finally {
        if (!cancelled) setTestStudentLoading(false);
      }
    };

    const debounce = setTimeout(fetchStudents, testStudentSearch ? 300 : 0);
    return () => {
      cancelled = true;
      clearTimeout(debounce);
    };
  }, [showTestDialog, recipientFilters, testStudentSearch]);

  // Auto-save disabled - using explicit "Save Draft" button instead
  // (Prevents unwanted draft creation)

  // Handle email field changes
  const handleEmailChange = (field, value) => {
    setEmail(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Handle recipient changes
  const handleRecipientsChange = (recipients) => {
    setEmail(prev => ({
      ...prev,
      recipients
    }));

    // Clear recipient errors
    if (errors.recipients) {
      setErrors(prev => ({
        ...prev,
        recipients: null
      }));
    }
  };

  // Handle dynamic field insertion
  const handleInsertDynamicField = (fieldCode) => {
    editorRef.current?.insertAtCursor(fieldCode);
  };

  // Handle attachment upload success
  const handleAttachmentUploadSuccess = (newAttachment) => {
    setAttachments(prev => [...prev, newAttachment]);
  };

  // Handle attachment delete success
  const handleAttachmentDeleteSuccess = (deletedUuid) => {
    setAttachments(prev => prev.filter(att => att.uuid !== deletedUuid));
  };

  // Validate email before proceeding
  const validatePage1 = () => {
    const newErrors = {};
    if (!email.email_type.trim()) {
      newErrors.email_type = 'Email type is required';
    }
    if (!email.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    if (!email.content.trim()) {
      newErrors.content = 'Email content is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next page
  const handleNextPage = async () => {
    if (validatePage1()) {
      // Auto-save as draft when moving to next page
      await handleSaveDraft();
      setWizardPage(2);
    }
  };

  // Handle send email
  const handleSendEmail = async () => {
    if (email.recipients.length === 0) {
      setErrors({ recipients: 'Please select at least one recipient' });
      return;
    }

    setLoading(true);
    try {
      let taskId = null;
      let campaignUuid = null;

      if (currentDraftId) {
        // Send existing draft (draft will be kept as reusable template)
        const sendData = {
          recipients: email.recipients,
          send_to_parents: false,
        };
        const response = await api.emails.sendDraft(currentDraftId, sendData);
        taskId = response.task_id;
        campaignUuid = currentDraftId; // Use draft UUID as campaign UUID
      } else {
        // Send as new campaign (returns task_id for progress tracking)
        const campaignData = {
          recipients: email.recipients,
          email_type: email.email_type,
          subject: email.subject,
          body_content: email.content,
          campaign_name: email.email_type,
          send_to_parents: false,
        };

        // Include draft_uuid if available (for copying attachments when resending)
        if (email.draft_uuid) {
          campaignData.draft_uuid = email.draft_uuid;
        }

        const response = await api.emails.sendCampaign(campaignData);
        taskId = response.task_id;
        campaignUuid = response.campaign_uuid; // Get campaign UUID from response
      }

      // Store task info in sessionStorage for Communications page to track (requires consent)
      if (taskId && campaignUuid && canUseStorage()) {
        sessionStorage.setItem('activeEmailTask', JSON.stringify({
          task_id: taskId,
          campaign_uuid: campaignUuid,
          campaign_name: email.email_type,
          recipient_count: email.recipients.length,
          started_at: new Date().toISOString()
        }));
      }

      // Show neutral toast - emails are being queued
      toast({
        title: t('email.campaignQueued', 'Emails Queued'),
        description: t('email.campaignQueuedDescription', {
          defaultValue: `${email.recipients.length} email(s) queued for sending.`,
          count: email.recipients.length
        }),
        variant: 'default',
        duration: 3000,
      });

      navigate('/communications');
    } catch (error) {
      // Error sending email - handled by error state
      toast({
        title: t('email.campaignSendFailed'),
        description: error.response?.data?.message || t('email.campaignSendFailedDescription'),
        variant: 'destructive',
      });
      setErrors({
        send: error.response?.data?.message || 'Failed to send email. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle save draft
  const handleSaveDraft = async (shouldNavigate = false) => {
    setSavingDraft(true);
    try {
      const draftData = {
        email_type: email.email_type,
        subject: email.subject,
        body_preview: email.content, // Store full content in body_preview for drafts
        campaign_name: email.email_type, // Use email_type as campaign name for template tracking
        notes: email.template_used ? `Template used: ${email.template_used}` : ''
      };

      let savedDraft;
      if (currentDraftId) {
        // Update existing draft
        savedDraft = await api.emails.updateDraft(currentDraftId, draftData);
      } else {
        // Create new draft
        savedDraft = await api.emails.createDraft(draftData);
        // Use uuid field (for external references) or id as fallback
        setCurrentDraftId(savedDraft.uuid || savedDraft.id);
      }

      // Draft saved successfully

      // Show success toast notification
      if (currentDraftId) {
        toast({
          title: t('email.draftUpdated'),
          description: t('email.draftUpdatedDescription'),
          variant: "success",
          duration: 3000,
        });
      } else {
        toast({
          title: t('email.draftSaved'),
          description: t('email.draftSavedDescription'),
          variant: "success",
          duration: 3000,
        });
      }

      // Clear any draft errors
      setErrors({ draft: null });

      // Navigate back to communications if requested
      if (shouldNavigate) {
        navigate('/communications');
      }

    } catch (error) {
      // Error saving draft - handled by toast notification

      // Show error toast notification
      toast({
        title: t('email.draftSaveFailed'),
        description: error.response?.data?.message || t('email.draftSaveFailedDescription'),
        variant: "destructive",
        duration: 5000,
      });

      setErrors({
        draft: error.response?.data?.message || 'Failed to save draft. Please try again.'
      });
    } finally {
      setSavingDraft(false);
    }
  };

  // Handle test email
  const handleSendTest = async () => {
    if (!testEmail) return;

    // Validate email content before sending test
    if (!email.subject || !email.content) {
      toast({
        title: t('email.validationError'),
        description: 'Please fill in subject and content before sending test email',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      await api.emails.sendTestEmail({
        test_email: testEmail,
        subject: email.subject,
        body_content: email.content,
        draft_uuid: currentDraftId || null,
        student_uuid: testStudentUuid || undefined,
      });

      toast({
        title: t('email.testSent'),
        description: t('email.testSentDescription', { email: testEmail }),
      });

      setShowTestDialog(false);
      setTestEmail('');
      setTestStudentUuid(null);
      setTestStudentSearch('');
    } catch (error) {
      // Error sending test email - handled by UI
      toast({
        title: t('email.testSendFailed'),
        description: error.response?.data?.error || error.response?.data?.message || t('email.testSendFailedDescription'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {wizardPage === 1
                  ? t('email.createNew')
                  : t('email.recipients')
                }
              </h1>
              <p className="text-muted-foreground">
                {wizardPage === 1
                  ? t('email.description')
                  : t('email.recipientsDescription')
                }
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Page indicator */}
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              {t('wizard.page')} {wizardPage} {t('wizard.of')} 2
            </div>

            <div className="flex items-center gap-2">
              {wizardPage === 1 && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setShowPreviewModal(true)}
                    disabled={!email.email_type || !email.subject || !email.content || !email.content.trim()}
                    className="flex items-center gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    {t('email.preview')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleSaveDraft(true)}
                    disabled={loading}
                    className="flex items-center gap-2"
                  >
                    <Save className="h-4 w-4" />
                    {t('email.saveAsDraft')}
                  </Button>
                </>
              )}

              {wizardPage === 2 && (
                <Button
                  variant="outline"
                  onClick={() => setShowPreviewModal(true)}
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  {t('email.preview')}
                </Button>
              )}


            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="w-full space-y-6">
          {wizardPage === 1 ? (
            /* Page 1: Email Details & Content */
            <>
              {/* Email Creation */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        {t('email.details')}
                      </CardTitle>
                      <CardDescription>{t('email.detailsDescription')}</CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDynamicFieldsModal(true)}
                      className="flex items-center gap-2"
                    >
                      <Type className="h-4 w-4" />
                      Insert Fields
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Email Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email-type">
                        {t('email.emailType')} *
                      </Label>
                      <Input
                        id="email-type"
                        placeholder="e.g., Welcome Letter, Payment Reminder"
                        value={email.email_type}
                        onChange={(e) => handleEmailChange('email_type', e.target.value)}
                        className={errors.email_type ? 'border-red-500' : ''}
                      />
                      {errors.email_type && (
                        <p className="text-sm text-red-500">{errors.email_type}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">
                        {t('email.subject')} *
                      </Label>
                      <Input
                        id="subject"
                        placeholder="Enter email subject line"
                        value={email.subject}
                        onChange={(e) => handleEmailChange('subject', e.target.value)}
                        className={errors.subject ? 'border-red-500' : ''}
                      />
                      {errors.subject && (
                        <p className="text-sm text-red-500">{errors.subject}</p>
                      )}
                    </div>
                  </div>

                  {/* Content Creation */}
                  <div className="space-y-2">
                    <Label htmlFor="email-content">
                      {t('email.emailContent')} *
                    </Label>
                    <RichTextEditor
                      ref={editorRef}
                      value={email.content}
                      onChange={(content) => handleEmailChange('content', content)}
                      placeholder="Start writing your email content here..."
                    />
                    {errors.content && (
                      <p className="text-sm text-red-500 mt-1">{errors.content}</p>
                    )}
                  </div>

                  {/* Attachments Section */}
                  <div className="space-y-2 pt-6 border-t">
                    <Label>
                      {t('email.attachments')}
                    </Label>
                    <AttachmentUploader
                      draftUuid={currentDraftId}
                      attachments={attachments}
                      onUploadSuccess={handleAttachmentUploadSuccess}
                      onDeleteSuccess={handleAttachmentDeleteSuccess}
                      onAutoSaveDraft={() => handleSaveDraft(false)}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Page 1 Navigation */}
              <div className="flex justify-between items-center pt-6 border-t">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('wizard.step1of2')}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowPreviewModal(true)}
                    disabled={!email.email_type || !email.subject || !email.content || !email.content.trim()}
                    className="flex items-center gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    {t('email.preview')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleSaveDraft(true)}
                    disabled={savingDraft || !email.email_type || !email.subject}
                    className="flex items-center gap-2"
                  >
                    <Save className="h-4 w-4" />
                    {savingDraft ? t('email.saving') : t('email.saveAsDraft')}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => navigate('/communications')}
                    className="flex items-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleNextPage}
                    disabled={savingDraft}
                    className="flex items-center gap-2"
                  >
                    {t('wizard.nextSelectRecipients')}
                    <ArrowLeft className="h-4 w-4 rotate-180" />
                  </Button>
                </div>
              </div>
            </>
          ) : wizardPage === 2 ? (
            /* Page 2: Recipients */
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    {t('email.selectRecipients')}
                    {email.recipients.length > 0 && (
                      <Badge variant="secondary">
                        {email.recipients.length} {t('email.selected')}
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>{t('email.recipientsDescription')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <RecipientSelector
                    selectedRecipients={email.recipients}
                    onRecipientsChange={handleRecipientsChange}
                    onFiltersChange={handleFiltersChange}
                    campaignName={email.email_type}
                  />
                  {errors.recipients && (
                    <Alert className="mt-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{errors.recipients}</AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Page 2 Navigation */}
              <div className="flex justify-between items-center pt-6 border-t">
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    onClick={() => setWizardPage(1)}
                    className="flex items-center gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t('wizard.backToContent')}
                  </Button>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {t('wizard.step2of2')}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowTestDialog(true)}
                    disabled={loading}
                    className="flex items-center gap-2"
                  >
                    <Send className="h-4 w-4" />
                    {t('email.test')}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => navigate('/communications')}
                    className="flex items-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleSendEmail}
                    disabled={loading || email.recipients.length === 0}
                    className="flex items-center gap-2"
                  >
                    <Send className="h-4 w-4" />
                    {t('email.send')} ({email.recipients.length})
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </div>

        {/* Dynamic Fields Modal */}
        {showDynamicFieldsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Type className="h-5 w-5" />
                      Insert Dynamic Fields
                    </CardTitle>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDynamicFieldsModal(false)}
                    className="h-8 w-8 p-0"
                  >
                    ×
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="overflow-auto max-h-[60vh] -mt-4">
                <DynamicFieldsSidebar
                  onInsertField={(fieldCode) => {
                    handleInsertDynamicField(fieldCode);
                    setShowDynamicFieldsModal(false);
                  }}
                  visible={true}
                  initialExpanded={[]}
                />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Test Email Dialog */}
        {showTestDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-lg mx-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5" />
                  {t('email.sendTestEmail')}
                </CardTitle>
                <CardDescription>
                  {t('email.testDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Recipient email */}
                <div className="space-y-2">
                  <Label htmlFor="test-email">{t('email.emailAddress')}</Label>
                  <Input
                    id="test-email"
                    type="email"
                    placeholder="test@example.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className={testStudentUuid && !testEmail ? 'border-red-500 focus-visible:ring-red-500' : ''}
                  />
                </div>

                {/* Student picker */}
                <div className="space-y-2">
                  <Label>{t('email.testStudent', 'Preview student')}</Label>
                  <p className="text-xs text-muted-foreground">
                    {t('email.testStudentHint', 'Template variables will render using this student\'s data. Showing students matching your recipient filters.')}
                  </p>

                  {/* Student search */}
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="pl-8"
                      placeholder={t('email.searchStudent', 'Search by name or ID…')}
                      value={testStudentSearch}
                      onChange={(e) => setTestStudentSearch(e.target.value)}
                    />
                  </div>

                  {/* Student list */}
                  <div className="border rounded-md divide-y max-h-52 overflow-y-auto">
                    {/* "No specific student" option */}
                    <button
                      type="button"
                      onClick={() => setTestStudentUuid(null)}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-muted/50 transition-colors ${!testStudentUuid ? 'bg-muted font-medium' : ''}`}
                    >
                      <span className="text-muted-foreground italic">{t('email.testStudentAny', 'First matching student (default)')}</span>
                    </button>

                    {testStudentLoading ? (
                      <div className="flex items-center justify-center py-4 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span className="text-sm">{t('common.loading', 'Loading…')}</span>
                      </div>
                    ) : testStudentList.length === 0 ? (
                      <div className="py-4 text-center text-sm text-muted-foreground">
                        {t('students.noStudentsFound', 'No students found')}
                      </div>
                    ) : (
                      testStudentList.map((student) => (
                        <button
                          key={student.uuid}
                          type="button"
                          onClick={() => setTestStudentUuid(student.uuid)}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-muted/50 transition-colors ${testStudentUuid === student.uuid ? 'bg-blue-50 dark:bg-blue-950' : ''}`}
                        >
                          <div className="flex flex-col min-w-0">
                            <span className="font-medium truncate">
                              {student.full_name || `${student.first_name || ''} ${student.last_name || ''}`.trim()}
                            </span>
                            <span className="text-xs text-muted-foreground font-mono">
                              {student.student_id}
                              {student.course_name && ` · ${student.course_name}`}
                            </span>
                          </div>
                          {/* Indicator icons */}
                          <div className="flex items-center gap-1.5 shrink-0">
                            {student.travel_required && (
                              <Bus className="h-3.5 w-3.5 text-blue-500" title={t('students.travelRequired', 'Bus travel')} />
                            )}
                            {student.is_cinnire && (
                              <Award className="h-3.5 w-3.5 text-amber-500" title={t('students.cinnire', 'Cinnire')} />
                            )}
                            {(student.house?.name || student.house_name) && (
                              <Home className="h-3.5 w-3.5 text-purple-600" title={student.house?.name || student.house_name} />
                            )}
                            {student.has_friends && (
                              <UserCheck className="h-3.5 w-3.5 text-green-500" title={t('students.hasFriends', 'Has friends')} />
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowTestDialog(false);
                      setTestStudentUuid(null);
                      setTestStudentSearch('');
                    }}
                  >
                    {t('email.cancel')}
                  </Button>
                  <Button
                    onClick={handleSendTest}
                    disabled={loading || !testEmail}
                    className="flex items-center gap-2"
                  >
                    <Send className="h-4 w-4" />
                    {loading ? t('email.sending') : t('email.send')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Email Preview Modal */}
        <EmailPreviewModal
          email={email}
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
        />
      </div>
    </div>
  );
}

export default EmailCreationWizard;